// frontend-public/src/services/apiService.js
// Simplified API service for public pages (no axios, no authService dependencies)

/**
 * 確定 API 基礎 URL
 */
const determineApiUrl = () => {
  // 優先使用環境變數
  if (process.env.REACT_APP_API_URL) {
    const envUrl = process.env.REACT_APP_API_URL;
    
    // 確保 URL 包含 /api 後綴
    return envUrl.endsWith('/api') ? envUrl : `${envUrl}/api`;
  }
  
  // 本地開發環境
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:5001/api';
  }
  
  // 生產環境 - 使用相同域名但不同路徑
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  
  // 使用同一域名的 /api 路徑
  return `${protocol}//${hostname}/api`;
};

// 設定 API 基礎 URL
const API_URL = determineApiUrl();
console.log('🔗 API 基礎 URL:', API_URL);

/**
 * 獲取認證頭部
 * @returns {Object} 包含認證令牌的頭部
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || 
                localStorage.getItem('token') || 
                localStorage.getItem('accessToken');
  
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

/**
 * 執行 API 請求 - 使用 fetch 而不是 axios
 * @param {String} method - HTTP 方法 (get, post, put, delete, patch)
 * @param {String} endpoint - API 端點路徑
 * @param {Object} data - 請求數據 (適用於 POST, PUT, PATCH)
 * @param {Boolean} isTest - 是否為測試請求 (無需認證)
 * @param {Object} customConfig - 自定義配置
 * @returns {Promise} 請求的 Promise
 */
const apiRequest = async (method, endpoint, data = null, isTest = false, customConfig = {}) => {
  try {
    console.log(`🚀 API 請求 (${method.toUpperCase()}) ${endpoint} 開始`);
    
    // 準備頭部
    const headers = isTest ? { 
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    } : getAuthHeaders();
    
    // 格式化 URL
    const apiUrl = !endpoint.startsWith('http') 
      ? `${API_URL}/${endpoint.replace(/^\/+/, '')}` 
      : endpoint;
    
    console.log(`📡 發送請求到: ${apiUrl}`);
    
    // 準備請求配置
    const config = {
      method: method.toUpperCase(),
      headers: headers,
      credentials: 'include', // 啟用 Cookie
      ...customConfig
    };
    
    // 添加請求主體（如果需要）
    if (data && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
      if (data instanceof FormData) {
        // FormData 不需要 Content-Type header
        delete config.headers['Content-Type'];
        config.body = data;
      } else {
        config.body = JSON.stringify(data);
      }
    }
    
    // 執行請求
    const response = await fetch(apiUrl, config);
    
    // 解析響應
    let responseData;
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }
    
    // 檢查響應狀態
    if (!response.ok) {
      // 處理 401 未授權錯誤
      if (response.status === 401 && window.location.pathname !== '/login') {
        console.error('🔐 認證失敗 (401) - 需要重新登入');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('companyId');
        
        // 保存當前路徑
        localStorage.setItem('redirectAfterLogin', window.location.pathname);
        
        // 重定向到登入頁面
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }
      
      // 拋出錯誤
      const error = new Error(`API 請求失敗: ${response.status}`);
      error.response = {
        status: response.status,
        data: responseData
      };
      throw error;
    }
    
    console.log(`✅ API 請求成功 (${method.toUpperCase()}) ${endpoint}`);
    
    // 返回與 axios 相似的響應格式
    return {
      data: responseData,
      status: response.status,
      headers: response.headers
    };
    
  } catch (error) {
    console.error(`❌ API 請求錯誤 (${method.toUpperCase()}) ${endpoint}:`, error);
    
    // 如果是網路錯誤
    if (!error.response) {
      error.response = {
        status: 0,
        data: { message: '網路錯誤 - 無法連接到伺服器' }
      };
    }
    
    throw error;
  }
};

/**
 * 測試 API 連接
 * @returns {Promise<boolean>} 連接是否成功
 */
const testApiConnection = async () => {
  try {
    console.log('🧪 測試 API 連接...');
    const response = await apiRequest('get', 'health', null, true);
    console.log('✅ API 連接測試成功:', response.data);
    return true;
  } catch (error) {
    console.error('❌ API 連接測試失敗:', error.message);
    return false;
  }
};

/**
 * 提交聯繫表單 (公開端點，無需認證)
 * @param {Object} formData - 表單數據
 * @returns {Promise} API 響應
 */
const submitContactInquiry = async (formData) => {
  try {
    console.log('📨 提交聯繫表單...');
    
    // 獲取當前頁面資訊
    const currentUrl = window.location.href;
    const currentPath = window.location.pathname;
    
    // 自動判斷來源頁面
    let source = 'unknown';
    if (currentPath.includes('pricing')) {
      source = 'pricing';
    } else if (currentPath.includes('faq')) {
      source = 'faq';
    } else if (currentPath === '/' || currentPath === '') {
      source = 'homepage';
    } else if (currentPath.includes('features')) {
      source = 'features';
    } else {
      source = 'other';
    }
    
    // 準備提交數據 - 使用 camelCase 來匹配後端
    const payload = {
      companyName: formData.companyName,
      contactName: formData.contactName,
      phone: formData.phone,
      email: formData.email,
      industry: formData.industry,
      message: formData.message || null,
      source: source,
      sourceUrl: currentUrl
    };
    
    // 發送請求 (isTest = true 表示不需要認證)
    const response = await apiRequest('post', 'contact/inquiry', payload, true);
    
    console.log('✅ 聯繫表單提交成功:', response.data);
    return response;
    
  } catch (error) {
    console.error('❌ 聯繫表單提交失敗:', error);
    throw error;
  }
};

// 導出服務
export default {
  API_URL,
  getAuthHeaders,
  apiRequest,
  testApiConnection,
  submitContactInquiry
};

// 也導出個別函數以兼容不同的導入方式
export {
  API_URL,
  getAuthHeaders,
  apiRequest,
  testApiConnection,
  submitContactInquiry
};